import { useRouter } from 'next/router';
import { Text } from '@music-vine/cadence/ui';
import { useSubscriptionType } from '@/hooks/auth';
import { useCurrency } from '@/hooks/billing/useCurrency';
import { ProrationBillingMode } from '@/hooks/billing/useProration';
import formatCurrency from '@/utils/formatCurrency';
import type { Plan } from '@/fixtures/plans';

/**
 * Describes what today's charge contains. Exclusive tax with a non-zero amount
 * is added on top, so the charge still includes it; only an exclusive zero-tax
 * charge excludes it. Unknown behaviour says nothing rather than guessing.
 */
const getTaxSentence = (taxBehavior: string | null | undefined, taxInPence: number) => {
  if (taxBehavior === 'inclusive') {
    return 'This price includes tax.';
  }
  if (taxBehavior === 'exclusive') {
    return taxInPence > 0 ? 'This price includes tax.' : 'This price does not include tax.';
  }
  return null;
};
const FinePrint = ({
  prorationPriceInPence,
  fullPriceInPence,
  billingMode,
  plan,
  subscriptionTerm,
  taxBehavior,
  taxInPence = 0,
  agreementLead,
  cardLast4
}: {
  prorationPriceInPence: number;
  /** Full price of the new plan; adds the "minus unused days" explainer. */
  fullPriceInPence?: number;
  billingMode: ProrationBillingMode;
  plan: Plan['name'];
  subscriptionTerm?: string;
  taxBehavior?: string | null;
  taxInPence?: number;
  /** Replaces "By upgrading to Uppbeat {plan}" for non-upgrade changes. */
  agreementLead?: string;
  /** Card the subscription will be charged to, from the proration preview. */
  cardLast4?: string | null;
}) => {
  const {
    locale
  } = useRouter();
  const currency = useCurrency();
  const currentPlan = useSubscriptionType();
  const currencyCode = currency?.code ?? 'GBP';
  const formatPrice = (valueInPence: number) => formatCurrency(valueInPence, locale ?? 'en-GB', currencyCode);
  const last4 = cardLast4;
  const subscriptionTermLabel = subscriptionTerm ?? `1 ${billingMode === ProrationBillingMode.MONTHLY ? 'month' : 'year'}`;
  const lead = agreementLead ?? `By upgrading to Uppbeat ${plan}`;
  const chargeSentence = `${lead}, you are agreeing to being charged ${formatPrice(prorationPriceInPence)}* today for a ${subscriptionTermLabel} subscription.`;
  const taxSentence = getTaxSentence(taxBehavior, taxInPence);
  return <>
      <Text className="mb-4 text-pretty text-sm" data-sentry-element="Text" data-sentry-source-file="FinePrint.tsx">
        {taxSentence ? `${chargeSentence} ${taxSentence}` : chargeSentence}
      </Text>
      {typeof fullPriceInPence === 'number' && <Text className="mb-4 text-pretty text-sm">
          This is {formatPrice(fullPriceInPence)} minus the price and duration
          of the days not yet used in your current Uppbeat {currentPlan} plan.
        </Text>}
      {!!last4 && <Text className="text-pretty text-sm">
          Your card ending in {last4} will be charged immediately for this
          purchase.
        </Text>}
      <Text className="my-4 text-pretty text-xs italic" data-sentry-element="Text" data-sentry-source-file="FinePrint.tsx">
        *Small billing adjustments may cause the final charge to vary by a few
        {currencyCode === 'GBP' ? ' pence' : ' cents'}.
      </Text>
    </>;
};
export default FinePrint;